import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Navigate, Link } from "react-router-dom";
import { FaArrowUp, FaArrowDown, FaEye } from "react-icons/fa";
import { getToken } from "../../api/auth";
import { getSalesHeaders } from "../../api/order";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./CompletedOrders.css";

const PAGE_SIZE = 50;

const CompletedOrders = () => {
  const token = getToken();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "no",
    direction: "desc",
  });

  // Export
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");

  const loadMoreRef = useRef(null);

  // Debounce user search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 600);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Build final search string sent to API
  const buildSearchQuery = (userSearch = "") => {
    // If user has entered search text, use only that
    if (userSearch) {
      return userSearch;
    }
    // If empty, default to "Finished"
    return "Finished";
  };

  const normalizeOrder = (o) => {
    const saleslines = Array.isArray(o.saleslines) ? o.saleslines : [];
    const hasQuantityDifference = saleslines.some(
      (line) =>
        line.quantity != null &&
        line.originalQuantity != null &&
        Number(line.quantity) !== Number(line.originalQuantity)
    );

    return {
      no: o.no || "",
      selltocustomername: o.sellToCustomerName || "",
      selltocustomerno: o.sellToCustomerNo || "",
      selltoaddress: o.sellToAddress || "",
      selltocity: o.sellToCity || "",
      documentdate: o.documentDate || "",
      assigneduserid: o.assignedUserId || "",
      status: o.status ?? null,
      headeramount: o.headerAmount ?? 0,
      salesLines: saleslines,
      hasQuantityDifference,
    };
  };

  const loadPage = useCallback(
    async (page, userSearch = "", isReset = false) => {
      if (!token) return;

      const finalSearch = buildSearchQuery(userSearch);

      if (isReset) {
        setLoading(true);
        setOrders([]);
      } else {
        setLoadingMore(true);
      }

      try {
        const res = await getSalesHeaders(
          page,
          PAGE_SIZE,
          finalSearch || undefined,
          "All"
        );

        if (res.success && Array.isArray(res.orders)) {
          const normalized = res.orders
            .map(normalizeOrder)
            // Extra safety: only keep completed orders
            .filter((order) => order.status === 3);

          setOrders((prev) => {
            if (isReset) return normalized;
            const existing = new Set(prev.map((o) => o.no));
            const newOnes = normalized.filter((o) => !existing.has(o.no));
            return [...prev, ...newOnes];
          });

          setHasMore(res.orders.length === PAGE_SIZE);
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.error("Failed to load completed orders:", err);
        setError("Failed to load completed orders.");
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [token]
  );

  // Reload when debounced search changes
  useEffect(() => {
    loadPage(1, debouncedSearch, true);
  }, [debouncedSearch, loadPage]);

  // Infinite scroll
  useEffect(() => {
    if (!hasMore || loadingMore || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const nextPage = Math.floor(orders.length / PAGE_SIZE) + 1;
          loadPage(nextPage, debouncedSearch);
        }
      },
      { rootMargin: "300px" }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, orders.length, debouncedSearch, loadPage]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedOrders = useMemo(() => {
    const list = [...orders];

    if (sortConfig.key) {
      list.sort((a, b) => {
        let A =
          sortConfig.key === "salesLines"
            ? a.salesLines.length
            : a[sortConfig.key];
        let B =
          sortConfig.key === "salesLines"
            ? b.salesLines.length
            : b[sortConfig.key];

        if (sortConfig.key === "hasQuantityDifference") {
          A = A ? 1 : 0;
          B = B ? 1 : 0;
        }
        if (typeof A === "string") A = A.toLowerCase();
        if (typeof B === "string") B = B.toLowerCase();

        return sortConfig.direction === "asc"
          ? A < B
            ? -1
            : 1
          : A > B
          ? -1
          : 1;
      });
    }

    return list;
  }, [orders, sortConfig]);

  const handleExportWithDates = () => {
    let data = sortedOrders;

    if (exportFromDate || exportToDate) {
      data = data.filter((order) => {
        if (!order.documentdate) return false;
        const date = new Date(order.documentdate);
        const from = exportFromDate ? new Date(exportFromDate) : null;
        const to = exportToDate ? new Date(exportToDate) : null;
        if (from) from.setHours(0, 0, 0, 0);
        if (to) to.setHours(23, 59, 59, 999);
        return (!from || date >= from) && (!to || date <= to);
      });
    }

    if (data.length === 0) {
      alert("No completed orders to export.");
      return;
    }

    const rows = data.map((o) => ({
      "Order No": o.no,
      "Customer Name": o.selltocustomername || "N/A",
      City: o.selltocity || "N/A",
      Address: o.selltoaddress || "N/A",
      "Document Date": o.documentdate
        ? new Date(o.documentdate).toLocaleDateString()
        : "-",
      "Assigned User": o.assigneduserid || "Unassigned",
      Status: "Completed",
      "Amount (€)": Number(o.headeramount).toFixed(2),
      "Sales Lines": o.salesLines.length,
      "Qty Diff": o.hasQuantityDifference ? "Yes" : "No",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CompletedOrders");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    const fileName = `CompletedOrders_${exportFromDate || "All"}_to_${
      exportToDate || "All"
    }_${new Date().toISOString().slice(0, 10)}.xlsx`;
    saveAs(blob, fileName);
  };

  if (!token) return <Navigate to="/login" replace />;

  return (
    <div className="completed-orders-container">
      <div className="completed-page-header">
        <h2 className="completed-orders-title">Completed Orders</h2>
        <p className="completed-page-subtitle">
          View all completed warehouse orders
        </p>
      </div>

      {error && <div className="completed-error-message">{error}</div>}

      <div className="completed-filters">
        <input
          type="text"
          placeholder="Search completed orders..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="completed-search-input"
        />
        <button
          className="completed-glass-btn completed-export-btn"
          onClick={() => setShowExportModal(true)}
          disabled={sortedOrders.length === 0}
        >
          Export To Excel
        </button>
      </div>

      <div className="completed-order-table-container">
        {loading && orders.length === 0 ? (
          <div className="completed-loading-container">
            <div className="completed-spinner"></div>
            <p>Loading completed orders...</p>
          </div>
        ) : sortedOrders.length === 0 ? (
          <div className="completed-empty-state">
            <p>
              {searchTerm
                ? "No completed orders found matching your search."
                : "No completed orders found."}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="completed-order-table">
              <thead>
                <tr>
                  {[
                    { key: "no", label: "Order No" },
                    { key: "selltocustomername", label: "Customer Name" },
                    { key: "selltocity", label: "City" },
                    { key: "selltoaddress", label: "Address" },
                    { key: "documentdate", label: "Document Date" },
                    { key: "assigneduserid", label: "Assigned User" },
                    { key: "status", label: "Status" },
                    { key: "headeramount", label: "Amount (€)" },
                    { key: "salesLines", label: "Sales Lines" },
                    { key: "hasQuantityDifference", label: "Qty Diff" },
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      className="completed-glass-header"
                      onClick={() => handleSort(key)}
                    >
                      {label}
                      {sortConfig.key === key && (
                        <span className="completed-sort-icon">
                          {sortConfig.direction === "asc" ? (
                            <FaArrowUp />
                          ) : (
                            <FaArrowDown />
                          )}
                        </span>
                      )}
                    </th>
                  ))}
                  <th className="completed-glass-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedOrders.map((order) => (
                  <tr key={order.no}>
                    <td>{order.no}</td>
                    <td>{order.selltocustomername || "N/A"}</td>
                    <td>{order.selltocity || "N/A"}</td>
                    <td>{order.selltoaddress || "N/A"}</td>
                    <td>
                      {order.documentdate
                        ? new Date(order.documentdate).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>{order.assigneduserid || "Unassigned"}</td>
                    <td>
                      <span
                        className="completed-status-indicator"
                        style={{ backgroundColor: "#22c55e" }}
                      ></span>{" "}
                      Completed
                    </td>
                    <td>€{Number(order.headeramount).toFixed(2)}</td>
                    <td>{order.salesLines.length}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={order.hasQuantityDifference}
                        readOnly
                      />
                    </td>
                    <td>
                      <Link
                        to={`/orders/${order.no}`}
                        className="completed-glass-btn completed-btn-primary"
                      >
                        <FaEye size={16} /> View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="completed-order-cards">
              {sortedOrders.map((order) => (
                <div key={order.no} className="completed-order-card">
                  <div>
                    <strong>Order No:</strong> {order.no}
                  </div>
                  <div>
                    <strong>Customer:</strong>{" "}
                    {order.selltocustomername || "N/A"}
                  </div>
                  <div>
                    <strong>City:</strong> {order.selltocity || "N/A"}
                  </div>
                  <div>
                    <strong>Date:</strong>{" "}
                    {order.documentdate
                      ? new Date(order.documentdate).toLocaleDateString()
                      : "-"}
                  </div>
                  <div>
                    <strong>User:</strong>{" "}
                    {order.assigneduserid || "Unassigned"}
                  </div>
                  <div>
                    <strong>Amount:</strong> €
                    {Number(order.headeramount).toFixed(2)}
                  </div>
                  <div>
                    <strong>Lines:</strong> {order.salesLines.length}
                  </div>
                  <div>
                    <strong>Qty Diff:</strong>{" "}
                    <input
                      type="checkbox"
                      checked={order.hasQuantityDifference}
                      readOnly
                    />
                  </div>
                  <Link
                    to={`/orders/${order.no}`}
                    className="completed-glass-btn completed-btn-primary"
                  >
                    <FaEye /> View
                  </Link>
                </div>
              ))}
            </div>

            {hasMore && (
              <div ref={loadMoreRef} style={{ height: 20, margin: "40px 0" }}>
                {loadingMore && <div className="completed-spinner"></div>}
              </div>
            )}

            {!hasMore && orders.length > 0 && (
              <div
                style={{ textAlign: "center", padding: "2rem", color: "#aaa" }}
              >
                All completed orders loaded ({orders.length})
              </div>
            )}
          </>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowExportModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Export Completed Orders</h3>
            <div className="modal-date-inputs">
              <div>
                <label>From</label>
                <input
                  type="date"
                  value={exportFromDate}
                  onChange={(e) => setExportFromDate(e.target.value)}
                />
              </div>
              <div>
                <label>To</label>
                <input
                  type="date"
                  value={exportToDate}
                  onChange={(e) => setExportToDate(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="completed-glass-btn cancel-btn"
                onClick={() => setShowExportModal(false)}
              >
                Cancel
              </button>
              <button
                className="completed-glass-btn export-confirm-btn"
                onClick={() => {
                  handleExportWithDates();
                  setShowExportModal(false);
                  setExportFromDate("");
                  setExportToDate("");
                }}
              >
                Export ({sortedOrders.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompletedOrders;
